import type { HeaderAction } from '~/components/orga/OrgaHeaderMenu.vue'

export type OrgaAddEntityType =
  | 'direction'
  | 'equipeDirection'
  | 'domaine'
  | 'equipeDomaine'
  | 'agence'

export type OrgaModifyKind = 'direction' | 'domaine' | 'equipe' | 'agence'

export type OrgaEntityModifyKind = 'direction' | 'domaine'

export type OrgaHeaderMenuParams = {
  to?: string
  addEntityType?: OrgaAddEntityType
  addContext?: Record<string, string | undefined>
  addLabel?: string
  modifyKind?: OrgaModifyKind
  entityModifyKind?: OrgaEntityModifyKind
  modifyEquipeLabel?: string
  accessEquipeLabel?: string
  groupEditIconUrl?: string
}

export type OrgaHeaderMenuSelectResult =
  | { kind: 'navigate'; to: string }
  | { kind: 'modify'; context: Record<string, string | undefined> }
  | { kind: 'add'; entityType: OrgaAddEntityType; context?: Record<string, string | undefined> }

export function resolveOrgaModifyLabel(
  modifyKind: OrgaModifyKind | undefined,
  modifyEquipeLabel: string | undefined,
): string {
  switch (modifyKind) {
    case 'direction':
      return 'Modifier direction'
    case 'domaine':
      return 'Modifier domaine'
    case 'agence':
      return 'Modifier agence'
    case 'equipe':
      return modifyEquipeLabel ?? 'Modifier le CODIR'
    default:
      return 'Modifier'
  }
}

export function resolveOrgaAddButtonLabel(
  addEntityType: OrgaAddEntityType | undefined,
  addLabel: string | undefined,
): string {
  if (addLabel) {
    return addLabel
  }
  switch (addEntityType) {
    case 'direction':
      return 'Ajouter direction'
    case 'domaine':
      return 'Ajouter domaine'
    case 'agence':
      return 'Ajouter agence'
    case 'equipeDirection':
    case 'equipeDomaine':
      return 'Ajouter CODIR'
    default:
      return 'Ajouter'
  }
}

function resolveEnterEntityLabel(params: OrgaHeaderMenuParams): string {
  return params.accessEquipeLabel ?? 'Accéder au CODIR'
}

function equipeHeaderAction(params: OrgaHeaderMenuParams): HeaderAction | null {
  if (params.modifyKind === 'equipe' && params.to) {
    return {
      id: 'modifyEquipe',
      label: params.modifyEquipeLabel ?? 'Modifier le CODIR',
      iconSvg: params.groupEditIconUrl,
      variant: 'secondary',
    }
  }
  if (params.addEntityType === 'equipeDirection' || params.addEntityType === 'equipeDomaine') {
    return { id: 'addEquipe', label: 'Ajouter CODIR', icon: 'group_add', variant: 'secondary' }
  }
  return null
}

function entityModifyHeaderAction(entityModifyKind: OrgaEntityModifyKind | undefined): HeaderAction | null {
  if (entityModifyKind === 'domaine') {
    return { id: 'modifyEntity', label: 'Modifier le domaine', icon: 'box_edit', variant: 'secondary' }
  }
  if (entityModifyKind === 'direction') {
    return { id: 'modifyEntity', label: 'Modifier la direction', icon: 'box_edit', variant: 'secondary' }
  }
  return null
}

function enterEntityHeaderAction(params: OrgaHeaderMenuParams): HeaderAction | null {
  if (!params.to) {
    return null
  }
  return {
    id: 'enterEntity',
    label: resolveEnterEntityLabel(params),
    variant: 'tertiary',
    icon: 'arrow_forward',
  }
}

export function buildOrgaElementHeaderActions(params: OrgaHeaderMenuParams): HeaderAction[] {
  const actions: HeaderAction[] = []
  const equipeAction = equipeHeaderAction(params)
  if (equipeAction) {
    actions.push(equipeAction)
  }
  const entityAction = entityModifyHeaderAction(params.entityModifyKind)
  if (entityAction) {
    actions.push(entityAction)
  }
  const enterAction = enterEntityHeaderAction(params)
  if (enterAction) {
    actions.push(enterAction)
  }
  return actions
}

export function hasOrgaElementHoverCard(
  libLong?: string | null,
  desc?: string | null,
): boolean {
  return Boolean(libLong) || Boolean(desc)
}

export type OrgaElementBlock =
  | 'add'
  | 'skeleton'
  | 'header-badge-menu'
  | 'header-badge'
  | 'badge'
  | 'header-button-menu'
  | 'modify'

export function resolveOrgaElementBlock(params: {
  status?: 'normal' | 'add'
  label?: string
  to?: string
  header?: boolean
  isEditing: boolean
  headerMenuCount: number
}): OrgaElementBlock {
  if (params.status === 'add') {
    return 'add'
  }
  if (params.label === undefined) {
    return 'skeleton'
  }
  const isBadge = params.to === undefined
  const showHeaderMenu = params.isEditing && params.headerMenuCount > 0
  if (params.header && isBadge && showHeaderMenu) {
    return 'header-badge-menu'
  }
  if (params.header && isBadge) {
    return 'header-badge'
  }
  if (isBadge) {
    return 'badge'
  }
  if (params.header && showHeaderMenu) {
    return 'header-button-menu'
  }
  return 'modify'
}

export function resolveOrgaHeaderMenuSelect(
  actionId: string,
  params: OrgaHeaderMenuParams,
): OrgaHeaderMenuSelectResult | null {
  if (actionId === 'enterEntity' && params.to) {
    return { kind: 'navigate', to: params.to }
  }
  if (actionId === 'modifyEquipe') {
    return {
      kind: 'modify',
      context: { ...params.addContext, modifyKind: 'equipe' },
    }
  }
  if (actionId === 'addEquipe' && params.addEntityType) {
    return {
      kind: 'add',
      entityType: params.addEntityType,
      context: params.addContext,
    }
  }
  if (actionId === 'modifyEntity' && params.entityModifyKind) {
    return {
      kind: 'modify',
      context: { ...params.addContext, modifyKind: params.entityModifyKind },
    }
  }
  return null
}
